import { useCallback, useEffect, useMemo } from 'react';

import type { ConnectStatementsInstitution } from './useConnectStatementsData';

import { useCachedResolvabilityResults, useUnknownResolvability } from 'services/quiltt/utils/quilttResolvability';

// Quiltt resolvability is async and can silently never resolve (#3505). After this ceiling the step renders
// with unresolved institutions shown as not-supported (but never auto-skipped — see ConnectStatements).
export const RESOLVABILITY_TIMEOUT_MS = 10_000;

/** Which statement path the member's institutions lead to, once every support check has settled. */
export type StatementsPath = 'auto' | 'manual' | 'mixed';

interface UseInstitutionSupportOptions {
    /**
     * Whether the timeout may start. True once the checks are actually running, or once it is known
     * they never will (no session token or connector) — otherwise a slow token would time out banks
     * nobody asked Quiltt about, and a missing one would hang the caller on its skeleton for good.
     */
    armTimeout: boolean;
    /** Banks the member already enabled: supported whatever the cache or the checks say. */
    enabledInstitutionIds?: ReadonlySet<string>;
}

interface UseInstitutionSupportResult {
    supportedInstitutionIds: string[];
    /** Every institution has a genuine answer. The only state allowed to drive an auto-skip. */
    allRealChecksComplete: boolean;
    /** Every institution has a genuine answer or has timed out — enough to stop showing a skeleton. */
    allSettled: boolean;
    /** true/false once known (a timeout reads as false), null while the check is still running. */
    getResolvable: (institutionId: string) => boolean | null;
    /** The check errored or timed out with no real answer — unknown, not known-unsupported. */
    isTimedOut: (institutionId: string) => boolean;
    /** Undefined until settled. A timed-out institution counts towards manual. */
    statementsPath: StatementsPath | undefined;
}

/**
 * Institution-level Auto Statements support, read from the connector-keyed resolvability cache that
 * `InstitutionResolvabilityCheckers` fills. The caller mounts the checkers; this only reads.
 *
 * Shared by the statements intro and ConnectStatements so the two cannot disagree about which banks
 * are supported. Errored and timed-out banks are kept as unknown in the same cache, so the next step
 * settles at once instead of waiting out the timeout again; they are never read as a real answer, and a
 * real answer that lands later wins.
 */
export default function useInstitutionSupport(
    institutions: ConnectStatementsInstitution[],
    connectorId: string | undefined,
    isLoading: boolean,
    { armTimeout, enabledInstitutionIds }: UseInstitutionSupportOptions
): UseInstitutionSupportResult {
    const { results: cachedResults } = useCachedResolvabilityResults(connectorId);
    const { unknownIds, markUnknown } = useUnknownResolvability(connectorId);

    // Real answers only: enabled banks win, and an errored check's fallback `false` is dropped.
    const resolvabilityResults = useMemo(() => {
        const real: Record<string, boolean> = {};
        Object.entries(cachedResults).forEach(([id, isResolvable]) => {
            if (!unknownIds.has(id)) {
                real[id] = isResolvable;
            }
        });
        enabledInstitutionIds?.forEach((id) => {
            real[id] = true;
        });
        return real;
    }, [cachedResults, unknownIds, enabledInstitutionIds]);

    const isUnknown = useCallback(
        (institutionId: string) => resolvabilityResults[institutionId] === undefined && unknownIds.has(institutionId),
        [resolvabilityResults, unknownIds]
    );

    const supportedInstitutionIds = useMemo(
        () =>
            institutions
                .filter((inst) => resolvabilityResults[inst.institutionId] === true)
                .map((inst) => inst.institutionId),
        [institutions, resolvabilityResults]
    );

    const allRealChecksComplete = useMemo(
        () => institutions.every((inst) => resolvabilityResults[inst.institutionId] !== undefined),
        [institutions, resolvabilityResults]
    );

    const allSettled = useMemo(
        () =>
            institutions.every(
                (inst) => resolvabilityResults[inst.institutionId] !== undefined || isUnknown(inst.institutionId)
            ),
        [institutions, resolvabilityResults, isUnknown]
    );

    // Failsafe for Quiltt's flaky resolvability (#3505): if a real result never arrives, mark the laggards
    // timed-out so the caller can render instead of hanging on the skeleton. The timer re-arms as real
    // results trickle in.
    useEffect(() => {
        if (!armTimeout || isLoading || institutions.length === 0 || allSettled) {
            return undefined;
        }
        const timer = setTimeout(() => {
            markUnknown(
                institutions
                    .filter((inst) => resolvabilityResults[inst.institutionId] === undefined)
                    .map((inst) => inst.institutionId)
            );
        }, RESOLVABILITY_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [armTimeout, isLoading, institutions, allSettled, resolvabilityResults, markUnknown]);

    const getResolvable = useCallback(
        (institutionId: string): boolean | null =>
            resolvabilityResults[institutionId] ?? (isUnknown(institutionId) ? false : null),
        [resolvabilityResults, isUnknown]
    );

    const statementsPath = useMemo<StatementsPath | undefined>(() => {
        if (isLoading || !allSettled) {
            return undefined;
        }
        if (supportedInstitutionIds.length === 0) {
            return 'manual';
        }
        return supportedInstitutionIds.length === institutions.length ? 'auto' : 'mixed';
    }, [isLoading, allSettled, supportedInstitutionIds.length, institutions.length]);

    return {
        supportedInstitutionIds,
        allRealChecksComplete,
        allSettled,
        getResolvable,
        isTimedOut: isUnknown,
        statementsPath,
    };
}
